import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowUpRightIcon, TriangleAlertIcon } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';
import { ATNA_CONFIG, VerticalConfig } from './atnaConfig';
import { useLanguage } from '../LanguageContext';
import { findFaqQuestion, resolveAnswer, questionLabel, VerificationScore, VerificationLabel, WhyThis, InformActNote } from './verification';
export function AtnaLauncher() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [typed, setTyped] = useState('');
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const location = useLocation();
  let verticalTag = 'home';
  if (location.pathname.includes('brossard')) verticalTag = 'brossard';
  const vertical: VerticalConfig = ATNA_CONFIG[verticalTag] || ATNA_CONFIG.other;
  useEffect(() => {
    if (location.pathname.includes('request-demo')) {
      setIsVisible(false);
      setIsOpen(false);
      return;
    }
    const handleScroll = () => setIsVisible(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
  useEffect(() => {
    const handleExternalOpen = () => {
      setIsOpen(true);
      trackEvent('atna_opened', {
        page: location.pathname,
        vertical: vertical.tag,
        location: 'external_trigger'
      });
    };
    window.addEventListener('atna:open', handleExternalOpen);
    return () => window.removeEventListener('atna:open', handleExternalOpen);
  }, [location.pathname, vertical.tag]);
  const pick = (question: string) => {
    setActiveQuestion(question);
    setExpanded(false);
    setSearchMessage(null);
    trackEvent('atna_question_clicked', {
      question,
      page: location.pathname,
      vertical: vertical.tag
    });
  };
  const submitSearch = () => {
    const matchingQuestion = findFaqQuestion(vertical.questions, typed);
    if (matchingQuestion) {
      pick(matchingQuestion);
      return;
    }
    setSearchMessage(isFR ? 'Aucune question correspondante. Essayez par exemple « intégration », « déploiement », « vérification » ou « démo ».' : 'No matching FAQ found. Try “integration,” “deployment,” “verification,” or “demo.”');
  };
  const handleOpen = () => {
    setIsOpen(true);
    trackEvent('atna_opened', {
      page: location.pathname,
      vertical: vertical.tag
    });
  };
  const handleClose = () => {
    setIsOpen(false);
    setActiveQuestion(null);
    setExpanded(false);
    setTyped('');
    setSearchMessage(null);
  };
  const reset = () => {
    setActiveQuestion(null);
    setExpanded(false);
    setTyped('');
    setSearchMessage(null);
  };
  const resolved = resolveAnswer(vertical, activeQuestion, isFR);
  return <>
      <AnimatePresence data-id="element-658" data-mp="kidtfkg86kqd">
        {isVisible && !isOpen && <motion.button initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} onClick={handleOpen} className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#121212] text-white px-5 py-4 rounded-none shadow-lg hover:bg-black transition-colors" data-id="element-659" data-mp="ygkhy9zab8dd">
            <span className="w-3 h-3 bg-[#4F46E5] -skew-x-[30deg]" aria-hidden="true" data-id="element-660" data-mp="1d4ot8sw0chm4" />
            <span className="font-sans text-sm font-medium tracking-wide" data-id="element-661" data-mp="1r5rg37uttvy3">
              {isFR ? 'Demander à ATNA' : 'Ask ATNA'}
            </span>
          </motion.button>}
      </AnimatePresence>

      <AnimatePresence data-id="element-662" data-mp="267ht631xst0a">
        {isOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 bottom-0 w-full md:w-[440px] bg-white z-50 shadow-2xl border-l border-[#E4E6EC] flex flex-col" data-id="element-663" data-mp="2a9l32n8yjpjg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-664" data-mp="1w8ihscpsej9l">
              <div className="font-sans text-xs font-semibold tracking-[0.15em] text-[#121212]/70 uppercase" data-id="element-665" data-mp="21a7udwsnr4zs">
                {isFR ? 'Assistant ATNA' : 'ATNA Assistant'}
              </div>
              <button onClick={handleClose} className="text-[#121212]/40 hover:text-[#121212] transition-colors" aria-label={isFR ? 'Fermer ATNA' : 'Close ATNA'} data-id="element-666" data-mp="3cmigjkcwc2v9">
                <XIcon className="w-5 h-5" data-id="element-667" data-mp="w3zlll22rk95" />
              </button>
            </div>

            <div className="px-5 py-2 border-b border-[#E4E6EC] bg-[#FAFAFB] font-sans text-[11px] text-[#5B6478]" data-id="element-668" data-mp="r93pa8pt2aq8">
              {isFR ? 'Démonstration · valeurs illustratives' : 'Demonstration · illustrative values'}
            </div>

            <div className="flex-1 overflow-y-auto p-5" data-id="element-669" data-mp="da479jwssu31">
              {!activeQuestion ? <div data-id="element-670" data-mp="8d3vcsd9ox40">
                  <h3 className="font-serif text-xl text-[#121212] leading-snug mb-1" data-id="element-671" data-mp="8ckn63df5n36">
                    {isFR ? 'Une intelligence vérifiée pour les communautés.' : 'Verified intelligence for communities.'}
                  </h3>
                  <p className="font-sans text-sm text-[#5B6478] mb-6" data-id="element-672" data-mp="1xs21msa0x6d0">
                    {isFR ? 'Explorez les questions fréquentes sur BCITI et ATNA.' : 'Explore frequently asked questions about BCITI and ATNA.'}
                  </p>
                  <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-3" data-id="element-673" data-mp="3i9t04wehhtwz">
                    {isFR ? 'Questions fréquentes' : 'Frequently asked questions'}
                  </div>
                  <div className="flex flex-col gap-2.5 mb-6" data-id="element-674" data-mp="eqhy495abv5g">
                    {vertical.questions.map(question => <button key={question} onClick={() => pick(question)} className="text-left border border-[#E4E6EC] bg-white rounded-lg px-4 py-3 font-sans text-sm text-[#121212] hover:border-[#4F46E5] transition-colors" data-id="element-675" data-mp="5s5wlc2vrcy8">
                        {questionLabel(question, isFR)}
                      </button>)}
                  </div>

                  <div className="flex items-center gap-2 border border-[#E4E6EC] px-3 py-2.5" data-id="element-676" data-mp="2my3b0z9pqfbs">
                    <input value={typed} onChange={event => {
                setTyped(event.target.value);
                setSearchMessage(null);
              }} onKeyDown={event => {
                if (event.key === 'Enter') submitSearch();
              }} placeholder={isFR ? 'Rechercher dans les questions fréquentes…' : 'Search the FAQ…'} className="flex-1 bg-transparent outline-none font-sans text-sm text-[#121212] placeholder:text-[#5B6478]" data-id="element-677" data-mp="2i0lb1ydobafx" />
                    <button onClick={submitSearch} aria-label={isFR ? 'Rechercher' : 'Search'} className="shrink-0 w-8 h-8 bg-[#4F46E5] text-white flex items-center justify-center hover:bg-[#4F46E5]/90 transition-colors" data-id="element-678" data-mp="1gjvj3ms9i8jn">
                      <ArrowUpRightIcon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-679" data-mp="14yyoqjhy92jd" />
                    </button>
                  </div>
                  {searchMessage && <p role="status" className="mt-2 font-sans text-[11px] text-[#5B6478]" data-id="element-680" data-mp="1dyvk13yugzne">
                      {searchMessage}
                    </p>}
                </div> : resolved && <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} data-id="element-681" data-mp="1snhx0z101fbg">
                    <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-2" data-id="element-682" data-mp="3h75mt3xevcl4">
                      {isFR ? 'Votre question' : 'You asked'}
                    </div>
                    <p className="font-serif text-base text-[#121212] mb-6" data-id="element-683" data-mp="388k6lffew9ni">
                      {questionLabel(activeQuestion, isFR)}
                    </p>

                    <div className="font-sans text-xs font-semibold tracking-wider text-[#4F46E5] uppercase mb-2" data-id="element-684" data-mp="4qs6tev1aler">
                      {isFR ? 'Réponse' : 'Answer'}
                    </div>
                    <p className="font-serif text-lg text-[#121212] leading-snug mb-6" data-id="element-685" data-mp="3r6cyznset16n">
                      {resolved.recommendation}
                    </p>

                    {resolved.caution && <div role="note" className="flex items-start gap-3 border border-amber-300 bg-amber-50 px-4 py-3 mb-6" data-id="element-686" data-mp="npnzumv5otr0">
                        <TriangleAlertIcon className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" data-id="element-687" data-mp="2laqrgyn5afj5" />
                        <p className="font-sans text-sm leading-relaxed text-amber-950" data-id="element-688" data-mp="2za06huzm73lj">
                          {resolved.caution}
                        </p>
                      </div>}

                    <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-3" data-id="element-689" data-mp="397wb03y1r0ao">
                      {isFR ? 'Points clés' : 'Key points'}
                    </div>
                    <div className="space-y-2 mb-6" data-id="element-690" data-mp="3iyr3hq1vyw4k">
                      {resolved.results.slice(0, 4).map(result => <div key={result} className="flex items-start gap-2.5" data-id="element-691" data-mp="3e0h41394p9d5">
                          <span className="w-1.5 h-1.5 bg-[#4F46E5] shrink-0 mt-2" aria-hidden="true" data-id="element-692" data-mp="3ry5my5jmy6fk" />
                          <span className="font-sans text-sm text-[#121212]" data-id="element-693" data-mp="afqg363quy5j">
                            {result}
                          </span>
                        </div>)}
                    </div>

                    <VerificationScore label={resolved.label} isFR={isFR} expanded={expanded} onToggle={() => {
              setExpanded(value => !value);
              if (!expanded) {
                trackEvent('atna_verification_expanded', {
                  page: location.pathname,
                  vertical: vertical.tag,
                  location: 'launcher'
                });
              }
            }} data-id="element-694" data-mp="1es6m8ajdyfk" />
                    <AnimatePresence initial={false} data-id="element-695" data-mp="34x299oc30z57">
                      {expanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} className="overflow-hidden" data-id="element-696" data-mp="39ukb6urome3p">
                          <div className="pt-4 space-y-4" data-id="element-697" data-mp="1pcjh8njeqr6n">
                            <VerificationLabel label={resolved.label} isFR={isFR} compact data-id="element-698" data-mp="1ke8a4kkhyus4" />
                            <WhyThis label={resolved.label} isFR={isFR} data-id="element-699" data-mp="8zckk3kbb8ws" />
                            <InformActNote isFR={isFR} data-id="element-700" data-mp="915ubrq82990" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    <div className="pt-6 mt-6 border-t border-[#E4E6EC]" data-id="element-701" data-mp="2plrc626wg4vm">
                      <button onClick={reset} className="font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-702" data-mp="2tm1pjzx0txki">
                        {isFR ? 'Poser une autre question' : 'Ask another question'}
                      </button>
                    </div>
                  </motion.div>}
            </div>

            <div className="p-5 border-t border-[#E4E6EC] bg-white" data-id="element-703" data-mp="3hcyqyir6xchi">
              <Link to={`/request-demo?source=atna&vertical=${vertical.tag}`} onClick={() => {
            trackEvent('atna_demo_clicked', {
              page: location.pathname,
              vertical: vertical.tag,
              location: 'launcher'
            });
            setIsOpen(false);
          }} className="flex items-center justify-center gap-2 w-full py-3 bg-[#4F46E5] text-white text-sm font-medium hover:bg-[#4F46E5]/90 transition-colors" data-id="element-704" data-mp="1ix2nokiexvzf">
                {isFR ? 'Demander une démo' : 'Request a demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-705" data-mp="8x86zlzwdfcz" />
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}